const API_URL = import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';

/** Email для повторного запроса magic link (подстановка в форму входа). */
export const VERBUM_LOGIN_EMAIL_KEY = 'verbumLoginEmail';
const LOGOUT_RESET_KEYS = [
  'authToken',
  'user',
  'verbum.chat.tabs.v1',
  'bible_navigation_history',
  'bible_history_position',
];

class AuthService {
  // Получить authToken из localStorage
  getAuthToken() {
    return localStorage.getItem('authToken');
  }

  // Установить authToken в localStorage
  setAuthToken(authToken) {
    localStorage.setItem('authToken', authToken);
  }

  // Очистить authToken
  clearAuthToken() {
    localStorage.removeItem('authToken');
  }

  // Полный сброс локальной сессии при выходе
  clearSessionOnLogout() {
    if (typeof localStorage === 'undefined') return;
    for (const key of LOGOUT_RESET_KEYS) {
      localStorage.removeItem(key);
    }
  }

  /** Сохранённый email для формы magic link (нормализованный). */
  getStoredLoginEmail() {
    if (typeof localStorage === 'undefined') return '';
    const raw = localStorage.getItem(VERBUM_LOGIN_EMAIL_KEY);
    if (raw) return raw.trim().toLowerCase();
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      if (u?.email) {
        const fromUser = String(u.email).trim().toLowerCase();
        if (fromUser) localStorage.setItem(VERBUM_LOGIN_EMAIL_KEY, fromUser);
        return fromUser;
      }
    } catch {
      /* ignore */
    }
    return '';
  }

  setStoredLoginEmail(email) {
    if (typeof localStorage === 'undefined') return;
    const normalized = String(email || '').trim().toLowerCase();
    if (normalized) localStorage.setItem(VERBUM_LOGIN_EMAIL_KEY, normalized);
    else localStorage.removeItem(VERBUM_LOGIN_EMAIL_KEY);
  }

  // Проверка валидности authToken
  async validateToken(authToken) {
    try {
      const response = await fetch(`${API_URL}/auth/validate`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      return response.ok;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }
  async getTestToken() {
    // Use Telegram auth with fake data for dev
    const response = await fetch(`${API_URL}/auth/telegram`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        telegramId: 123456789, // Fake telegram ID for dev
        firstName: 'Dev',
        lastName: 'User',
        username: 'elevenz_z'
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to get dev token');
    }

    const result = await response.json();
    const token = result.token || result.accessToken;
    if (token) {
      localStorage.setItem('authToken', token);
      if (result.user) localStorage.setItem('user', JSON.stringify(result.user));
    }
    return result;
  }

  async exchangeToken(oneTimeToken) {
    const response = await fetch(`${API_URL}/auth/exchange-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: oneTimeToken }),
    });

    if (!response.ok) {
      throw new Error('Failed to exchange token');
    }

    const result = await response.json();
    const token = result.token || result.accessToken;
    if (token) {
      localStorage.setItem('authToken', token);
    }
    return result;
  }

  async requestMagicLink(email, redirectBaseUrl = window.location.origin) {
    const response = await fetch(`${API_URL}/auth/magic-link/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, redirectBaseUrl }),
    });
    if (!response.ok) {
      throw new Error('Failed to request magic link');
    }
    const normalized = String(email || '').trim().toLowerCase();
    if (normalized) this.setStoredLoginEmail(normalized);
    return response.json();
  }

  async verifyMagicLink(token) {
    const response = await fetch(`${API_URL}/auth/magic-link/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Magic link is invalid or expired');
    }
    const result = await response.json();
    if (result.token) {
      localStorage.setItem('authToken', result.token);
      if (result.user) localStorage.setItem('user', JSON.stringify(result.user));
      if (result.user?.email) this.setStoredLoginEmail(result.user.email);
    }
    return result;
  }

  // Authenticate with Telegram Web App
  async authenticateWithTelegram(telegramUserData) {
    console.log('🔍 Telegram user data:', telegramUserData);
    
    // Безопасная проверка данных
    if (!telegramUserData || !telegramUserData.id) {
      throw new Error('Invalid Telegram data');
    }

    try {
      // Получаем initData из Telegram Web App для безопасной проверки
      const { telegramService } = await import('./telegram');
      const initData = telegramService.isTelegramWebApp ? telegramService.getInitData() : null;
      
      const requestBody = initData 
        ? { initData: initData }  // Безопасный вариант с проверкой подписи
        : {                        // Fallback для разработки (только если не в Telegram)
        telegramId: telegramUserData.id,
        firstName: telegramUserData.firstName,
        lastName: telegramUserData.lastName,
        username: telegramUserData.username
          };

      const response = await fetch(`${API_URL}/auth/telegram`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to authenticate with Telegram');
    }

    const result = await response.json();
    
    // Save authToken to localStorage
    if (result.token) {
      localStorage.setItem('authToken', result.token);
        if (result.user) {
          localStorage.setItem('user', JSON.stringify(result.user));
        }
      console.log('💾 AuthToken saved to localStorage after Telegram auth');
    }
    
    return result;
    } catch (error) {
      console.error('Telegram authentication error:', error);
      throw error;
    }
  }

}

export const authService = new AuthService();
